import { useMemo } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import CruiseLuxPlusLabel from 'components/Cruises/CruiseLuxPlusLabel'
import Group from 'components/utils/Group'
import BodyText from 'LuxKit/Typography/BodyText'
import OfferInclusionItem from 'components/Common/Inclusions/OfferInclusionItem'
import { getIsEnabledLuxPlusCruiseInclusions } from 'luxPlus/selectors/featureToggle'
import CruiseLuxPlusOnboardCreditLabel from 'components/Cruises/CruiseInclusion/CruiseLuxPlusOnboardCreditLabel'
import { useAppSelector } from 'hooks/reduxHooks'
import { buildAllStandardInclusions } from 'lib/cruises/cruiseInclusionsUtils'
import CruiseEvergreenInclusionList from './CruiseEvergreenInclusionList'

const EVERGREEN_VISIBLE_ITEMS_WITH_STANDARD_INCLUSIONS = 1
const EVERGREEN_VISIBLE_ITEMS_WITHOUT_STANDARD_INCLUSIONS = 3

const InclusionItemWrapper = styled.div`
  min-width: 0;
`

const SelectPackagesLabel = styled(BodyText)`
  white-space: nowrap;
`

interface Props {
  evergreenInclusions?: Array<App.PackageInclusion>
  tileInclusions?: Array<App.OfferInclusion>
  standardInclusions: Array<App.PackageInclusion>
  luxPlusInclusions: Array<App.PackageInclusion>
  canSeeLuxPlusLabel?: boolean
  offer?: App.CruiseOffer
}

function CruiseInclusionListHomeTile({
  standardInclusions,
  evergreenInclusions,
  tileInclusions,
  luxPlusInclusions,
  canSeeLuxPlusLabel,
  offer,
}: Props) {
  const intl = useIntl()
  const isEnabledLuxPlusCruiseInclusions = useAppSelector(getIsEnabledLuxPlusCruiseInclusions)

  const allStandardInclusions = useMemo(() => {
    return buildAllStandardInclusions(standardInclusions, tileInclusions, false)
  }, [standardInclusions, tileInclusions])

  const hasStandardInclusions = !!allStandardInclusions.length
  const hasLuxPlusInclusions = isEnabledLuxPlusCruiseInclusions && luxPlusInclusions.length > 0
  const hasInclusions = hasStandardInclusions || hasLuxPlusInclusions || canSeeLuxPlusLabel || !!evergreenInclusions?.length
  const evergreenVisibleItems = hasStandardInclusions ?
    EVERGREEN_VISIBLE_ITEMS_WITH_STANDARD_INCLUSIONS :
    EVERGREEN_VISIBLE_ITEMS_WITHOUT_STANDARD_INCLUSIONS

  const standardInclusionSelectPackagesText = `(${intl.formatMessage({
    id: 'gWMOgND2ZhSab_HT-_aV',
    defaultMessage: 'Available on select packages',
    description: 'Note indicating inclusions are available on select cruise packages',
  })})`
  const hasMultipleStandardInclusions = allStandardInclusions.length > 1

  return <>
    {hasInclusions && <Group direction="vertical" gap={16} horizontalAlign="start">
      {(canSeeLuxPlusLabel || hasLuxPlusInclusions) && (
        <Group direction="vertical" gap={8} verticalAlign="center">
          {canSeeLuxPlusLabel && <CruiseLuxPlusLabel
            isModalEnabled
            offer={offer}
          />}

          {hasLuxPlusInclusions && (
            <CruiseLuxPlusOnboardCreditLabel
              luxPlusInclusions={luxPlusInclusions}
              offer={offer}
            />
          )}
        </Group>
      )}

      {hasStandardInclusions && (
        <Group
          direction={hasMultipleStandardInclusions ? 'vertical' : 'horizontal'}
          gap={hasMultipleStandardInclusions ? 4 : '0 4'}
          wrap="wrap"
          verticalAlign="center"
          horizontalAlign="start"
        >
          {allStandardInclusions.map((inclusion) => <InclusionItemWrapper key={inclusion.id}>
            <OfferInclusionItem
              inclusion={inclusion}
              colour="highlight-primary-dark"
              isHighlighted
              showMinStayDescription
            />
          </InclusionItemWrapper>)}
          <SelectPackagesLabel variant="small" colour="normal-contrast">
            {standardInclusionSelectPackagesText}
          </SelectPackagesLabel>
        </Group>
      )}

      {!!evergreenInclusions?.length && <CruiseEvergreenInclusionList
        evergreenInclusions={evergreenInclusions}
        visibleItems={evergreenVisibleItems}
      />}
    </Group>}
  </>
}

export default CruiseInclusionListHomeTile
